// Créances d'ouverture (10/10/2026) : factures Dolibarr de l'exercice
// antérieur encore impayées. Aperçu d'abord (rien n'est créé), puis création
// en à-nouveau PROVISOIRE (411 / 445740 / 890000), contre-passé à l'import
// du FEC de l'expert-comptable. À défaut de liste des impayés : les PDF
// Dolibarr des factures (ou un ZIP), lus sans IA, passent par le même aperçu.
import { useState } from "react";
import JSZip from "jszip";
import api from "../services/api";

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});
const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const frDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

export default function OpeningReceivablesModal({ companyId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [pdfRows, setPdfRows] = useState(null);     // lignes lues dans les PDF
  const [pdfRefused, setPdfRefused] = useState([]); // PDF non lus (motif)
  const [progress, setProgress] = useState("");
  const [preview, setPreview] = useState(null);
  const [choices, setChoices] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function send(confirm) {
    setBusy(true); setError("");
    try {
      const payload = pdfRows
        ? { rows: pdfRows, confirm, client_choices: choices }
        : { content: await toBase64(file), filename: file.name, confirm, client_choices: choices };
      const r = await api.salesOcrInvoices.dolibarrOpening(companyId, payload);
      if (confirm) { setResult(r); onDone && onDone(); } else setPreview(r);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  // PDF (ou ZIP) : lecture une par une, sans IA, puis aperçu
  async function pickPdfs(list) {
    setBusy(true); setError(""); setPreview(null); setChoices({}); setFile(null);
    const pdfs = [];
    for (const f of list) {
      if (/\.zip$/i.test(f.name)) {
        try {
          const zip = await JSZip.loadAsync(f);
          for (const z of Object.values(zip.files).filter(z => !z.dir && /\.pdf$/i.test(z.name) && !/(^|\/)__MACOSX\//.test(z.name))) {
            pdfs.push(new File([await z.async("blob")], z.name.split("/").pop(), { type: "application/pdf" }));
          }
        } catch { setError(`ZIP illisible : ${f.name}`); }
      } else pdfs.push(f);
    }
    const rows = [], refused = [];
    for (let i = 0; i < pdfs.length; i++) {
      setProgress(`Lecture des PDF : ${i + 1}/${pdfs.length}…`);
      try { const r = await api.salesOcrInvoices.dolibarrOpeningPdf(companyId, pdfs[i]); rows.push(r.row); }
      catch (e) { refused.push(`${pdfs[i].name} : ${e.message}`); }
    }
    setProgress(""); setPdfRows(rows); setPdfRefused(refused);
    try {
      if (rows.length) setPreview(await api.salesOcrInvoices.dolibarrOpening(companyId, { rows, confirm: false }));
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  // Mode PDF : retirer des lignes (facture réglée, erreur) puis relancer l'aperçu
  async function removeRows(refs) {
    const rows = (pdfRows || []).filter(x => !refs.includes(String(x.invoice_ref || "").toUpperCase().replace(/\s+/g, "")));
    setPdfRows(rows); setBusy(true); setError("");
    try { setPreview(rows.length ? await api.salesOcrInvoices.dolibarrOpening(companyId, { rows, confirm: false, client_choices: choices }) : null); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const rowErrors = (r) => r.errors.filter(m => !(choices[r.invoice_ref] && /Client à confirmer/.test(m)));
  const blocking = preview ? preview.preview.filter(r => rowErrors(r).length).length : 0;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="card" style={{ width: 860, maxWidth: "96vw", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Créances d'ouverture (factures impayées de l'exercice antérieur)</div>
          <button className="btn" onClick={onClose} disabled={busy}>✕</button>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>
          Export Dolibarr (CSV ou Excel) des factures <strong>impayées à la clôture</strong> : Réf. facture, Date, Tiers, Total HT, TVA, TTC et, pour les factures partiellement réglées, <strong>Reste à payer</strong>.
          Aucun chiffre d'affaires n'est créé : chaque facture devient une créance client en <strong>à-nouveau provisoire</strong> au premier jour de l'exercice, rapprochable avec la banque.
          À l'import du FEC de votre expert-comptable, ces à-nouveaux sont contre-passés automatiquement et vos lettrages reportés.
        </div>

        {!result && (
          <label className="btn primary" style={{ alignSelf: "flex-start", cursor: busy ? "wait" : "pointer", marginBottom: 10 }}>
            {file ? `Fichier : ${file.name} — changer` : pdfRows ? `${pdfRows.length} PDF lu(s) — changer` : "+ Choisir l'export des impayés, ou les PDF (ZIP) des factures"}
            <input type="file" multiple accept=".csv,.xlsx,.xls,.pdf,.zip,application/pdf" disabled={busy} style={{ display: "none" }}
              onChange={e => {
                const list = Array.from(e.target.files || []); e.target.value = "";
                const sheet = list.find(f => /\.(csv|xlsx|xls)$/i.test(f.name));
                if (sheet) { setFile(sheet); setPdfRows(null); setPdfRefused([]); setPreview(null); setChoices({}); }
                else if (list.length) pickPdfs(list);
              }} />
          </label>
        )}
        {!file && !pdfRows && !result && (
          <div style={{ fontSize: 12, color: "#b45309", marginBottom: 10 }}>
            Sans la liste des impayés : sélectionnez les PDF Dolibarr des factures de l'exercice antérieur encore impayées (ou un ZIP). Les PDF marqués « Reste à payer 0 » sont écartés ; vérifiez l'aperçu et retirez toute facture déjà réglée.
          </div>
        )}
        {progress && <div style={{ fontSize: 12.5, marginBottom: 8 }}>{progress}</div>}
        {pdfRefused.length > 0 && (
          <div style={{ fontSize: 12, color: "#e24b4a", marginBottom: 8 }}>{pdfRefused.length} PDF non lu(s) : {pdfRefused.slice(0, 5).join(" ; ")}{pdfRefused.length > 5 ? "…" : ""}</div>
        )}
        {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 10, fontSize: 13 }}>{error}</div>}

        {result && (
          <div style={{ fontSize: 13, padding: "10px 12px", background: "#f0fdf9", borderRadius: 8, marginBottom: 10 }}>
            ✓ {result.created.length} créance(s) d'ouverture créée(s) au {frDate(result.opening_date)}, pour {eur(result.total_ttc)} (dont TVA {eur(result.total_tva)}).
            Elles apparaissent dans vos factures, en attente de règlement.
          </div>
        )}

        {preview && !result && (
          <>
            <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>
              Aperçu : {preview.valid} créance(s) valide(s) sur {preview.rows} · total {eur(preview.total_ttc)} · à-nouveau au {frDate(preview.opening_date)}
              {" "}· TVA {preview.vat_exigibility === "encaissements" ? "sur les encaissements (en attente en 445740)" : "sur les débits (déjà déclarée)"}
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--text-muted)" }}>
                    <th>Facture</th><th>Date</th><th>Client</th><th style={{ textAlign: "right" }}>TTC</th><th style={{ textAlign: "right" }}>Restant dû</th><th>Statut</th>{pdfRows && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map(r => {
                    const errs = rowErrors(r);
                    return (
                      <tr key={r.invoice_ref} style={{ borderTop: "1px solid #eee", background: errs.length ? "#fef2f2" : undefined }}>
                        <td>{r.invoice_ref}</td>
                        <td>{frDate(r.invoice_date)}</td>
                        <td>
                          {r.client_name}
                          {r.match_status === "to_confirm" && (
                            <select value={choices[r.invoice_ref] || ""} onChange={e => setChoices(c => ({ ...c, [r.invoice_ref]: e.target.value === "new" ? "new" : (e.target.value ? Number(e.target.value) : undefined) }))} style={{ display: "block", fontSize: 11, marginTop: 2 }}>
                              <option value="">— Choisir le client —</option>
                              {r.candidates.map(c => <option key={c.id} value={c.id}>{c.name}{c.postal_code ? ` (${c.postal_code})` : ""}</option>)}
                              <option value="new">Nouveau client</option>
                            </select>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>{eur(r.amount_ttc)}</td>
                        <td style={{ textAlign: "right" }}>{eur(r.due_ttc)}</td>
                        <td style={{ color: errs.length ? "#e24b4a" : r.warnings?.length ? "#b45309" : "#1a9e72" }}>{errs.length ? errs.join(" ") : r.warnings?.length ? `⚠ ${r.warnings.join(" ")}` : (r.match_status === "new" ? "✓ nouveau client" : "✓")}</td>
                        {pdfRows && <td><button className="btn" style={{ fontSize: 10, padding: "1px 6px" }} disabled={busy} onClick={() => removeRows([r.invoice_ref])}>Retirer</button></td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {blocking > 0 && (
              <div style={{ fontSize: 12, color: "#b45309", marginTop: 8 }}>
                ⚠️ {blocking} ligne(s) en erreur : {pdfRows ? "retirez-les" : "retirez-les du fichier"} (ou choisissez le client) puis relancez l'aperçu.
                {pdfRows && <button className="btn" style={{ fontSize: 11, padding: "2px 8px", marginLeft: 8 }} disabled={busy}
                  onClick={() => removeRows(preview.preview.filter(r => rowErrors(r).length).map(r => r.invoice_ref))}>Retirer les lignes en erreur</button>}
              </div>
            )}
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="btn" onClick={onClose} disabled={busy}>Fermer</button>
          {!result && (file || pdfRows) && <button className="btn" disabled={busy} onClick={() => send(false)}>{busy && !preview ? "Analyse…" : "Aperçu"}</button>}
          {!result && preview && (
            <button className="btn primary" disabled={busy || blocking > 0 || !preview.valid} onClick={() => send(true)}>
              {busy ? "Création…" : `Créer ${preview.preview.length - blocking} créance(s)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
